import { X, Undo2, Type, Pencil, Crop, Check, Shapes, RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ToolType } from "./types";

interface TopBarProps {
    onClose: () => void;
    onUndo: () => void;
    canUndo: boolean;
    selectedTool: ToolType | null;
    selectedColor: string;
    onToolChange: (tool: ToolType | null) => void;
    onCropConfirm?: () => void;
    onCropCancel?: () => void;
    /** One clockwise quarter-turn, applied immediately. Shown left of Crop; hidden while cropping. */
    onRotate?: () => void;
}

function getContrastColor(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    // Relative luminance (ITU-R BT.709)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
}

const TOOLS: { id: ToolType; icon: React.ElementType; label: string }[] = [
    { id: "crop", icon: Crop, label: "Crop" },
    { id: "text", icon: Type, label: "Text" },
    { id: "shapes", icon: Shapes, label: "Shapes" },
    { id: "pen", icon: Pencil, label: "Pen" },
];

export function TopBar({
    onClose,
    onUndo,
    canUndo,
    selectedTool,
    selectedColor,
    onToolChange,
    onCropConfirm,
    onCropCancel,
    onRotate,
}: TopBarProps) {
    const isCropActive = selectedTool === "crop";

    return (
        <div
            className="bg-black px-4 pb-4 flex items-center gap-2 sm:gap-3 z-10"
            style={{ paddingTop: "max(1rem, env(safe-area-inset-top, 1rem))" }}
        >
            {isCropActive ? (
                <>
                    {/* Cancel button — exits crop mode without applying */}
                    <button
                        type="button"
                        onClick={onCropCancel}
                        className="h-10 px-2 flex items-center justify-center shrink-0"
                        aria-label="Cancel crop"
                    >
                        <span className="text-sm font-medium text-white">Cancel</span>
                    </button>

                    <div className="flex-1" />

                    {/* Confirm button — applies the crop */}
                    <button
                        type="button"
                        onClick={onCropConfirm}
                        className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shrink-0"
                        aria-label="Confirm crop"
                    >
                        <Check className="w-5 h-5" />
                    </button>
                </>
            ) : (
                <>
                    {/* Close button — exits editor entirely */}
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-white shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex-1" />

                    {/* Undo — visible when there's something to undo (pen, shapes, or no active tool) */}
                    {(selectedTool === null || selectedTool === "pen" || selectedTool === "shapes") && canUndo && (
                        <button
                            type="button"
                            onClick={onUndo}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 transition-opacity"
                            aria-label="Undo"
                        >
                            <Undo2 className="w-5 h-5" />
                        </button>
                    )}

                    {/* Rotate + tool buttons. Spacing tightens below `sm` so Close,
                        Undo and all five buttons fit a 375px-wide screen. */}
                    <div className="flex items-center gap-2 sm:gap-3">
                        {/* Rotate — a one-tap action, not a tool: it applies
                            immediately and never takes the active state. */}
                        {onRotate && (
                            <button
                                type="button"
                                onClick={onRotate}
                                className="w-10 h-10 rounded-full bg-zinc-800 text-white/80 flex items-center justify-center shrink-0 touch-manipulation"
                                aria-label="Rotate 90 degrees"
                            >
                                <RotateCw className="w-5 h-5" />
                            </button>
                        )}

                        {/* Tool buttons — always visible, active tool gets colored background */}
                        {TOOLS.map(({ id, icon: Icon, label }) => {
                            const isActive = id === selectedTool;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => onToolChange(isActive ? null : id)}
                                    className={cn(
                                        "w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors touch-manipulation",
                                        !isActive && "bg-zinc-800 text-white/80"
                                    )}
                                    style={isActive ? { backgroundColor: selectedColor, color: getContrastColor(selectedColor) } : undefined}
                                    aria-label={label}
                                >
                                    <Icon className="w-5 h-5" />
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
